import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, ArrowLeft, Check, Star, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { copy } from '@/copy';

const SummaryPage = () => {
  const { user, axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [reflections, setReflections] = useState([]);
  const [foundingMemberStatus, setFoundingMemberStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = axiosAuth();
        
        // Fetch summary (only available if all checkpoints complete)
        const summaryRes = await api.get('/reflections/summary/all');
        setReflections(summaryRes.data);
        
        // Fetch founding member status
        const foundingRes = await api.get('/founding-member/status');
        setFoundingMemberStatus(foundingRes.data);
      } catch (error) {
        console.error('Error fetching summary:', error);
        if (error.response?.status === 403) {
          toast.error(copy.summary.incompleteError);
          navigate('/dashboard');
        } else {
          toast.error(copy.summary.loadError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [axiosAuth, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1D2D44] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1D2D44] font-medium">{copy.summary.loadingSummary}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/dashboard" 
            className="flex items-center text-[#424E59] hover:text-[#1D2D44] transition-colors"
            data-testid="back-to-dashboard-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{copy.nav.backToJourney}</span>
          </Link>
          
          <span className="font-serif text-lg text-[#1D2D44]">
            Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-1" strokeWidth={1.5} /> Connection
          </span>
          
          <div className="w-20"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Completion Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="w-20 h-20 bg-[#5E7F63] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1D2D44] mb-3">
            {copy.summary.title}
          </h1>
          <p className="text-[#3A4A5A] max-w-lg mx-auto">
            {copy.summary.congratulations(user?.name?.split(' ')[0])}
          </p>
        </div>

        {/* Summary Content - Print-like style */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8 animate-slide-up">
          <div className="bg-[#1D2D44] p-6 text-center">
            <p className="text-[#B89B5E] text-sm uppercase tracking-widest mb-2">{copy.summary.summaryLabel}</p>
            <h2 className="font-serif text-2xl text-white">
              {copy.summary.summaryTitle}
            </h2>
          </div>
          
          <div className="p-6 sm:p-10">
            {reflections.map((reflection, index) => (
              <div 
                key={reflection.id}
                className={`${index > 0 ? 'border-t border-gray-100 pt-8 mt-8' : ''}`}
                data-testid={`summary-reflection-${reflection.checkpoint_id}`}
              >
                {/* Checkpoint Header */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full bg-[#1D2D44] flex items-center justify-center text-[#B89B5E] text-sm font-medium">
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-serif text-xl text-[#1D2D44]">{reflection.checkpoint_name}</h3>
                    <p className="text-sm text-[#5E7F63]">{reflection.checkpoint_subtitle}</p>
                  </div>
                </div>

                {/* Serious Reflection */}
                <div className="ml-11">
                  <p className="text-[#3A4A5A] text-sm italic mb-2">
                    {reflection.serious_question}
                  </p>
                  <p className="text-[#1D2D44] leading-relaxed font-serif whitespace-pre-wrap">
                    {reflection.serious_answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Founding Member Offer - Only shown after completion */}
        {foundingMemberStatus?.is_eligible && foundingMemberStatus?.is_available && (
          <Card className="border-none shadow-lg rounded-2xl overflow-hidden mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <div className="bg-gradient-to-r from-[#1D2D44] to-[#1F1F1F] p-1">
              <div className="bg-white rounded-t-xl">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#B89B5E]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="w-6 h-6 text-[#B89B5E]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[#B89B5E] text-sm uppercase tracking-widest mb-1">
                        {copy.foundingMember.label}
                      </p>
                      <h3 className="font-serif text-2xl text-[#1D2D44] mb-2">
                        {copy.foundingMember.title}
                      </h3>
                      <p className="text-[#1D2D44] mb-4 leading-relaxed">
                        {copy.foundingMember.description}
                      </p>
                      
                      <div className="bg-[#F5F5F5] rounded-xl p-4 mb-4">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="font-serif text-3xl text-[#1D2D44]">{copy.foundingMember.priceLabel(foundingMemberStatus.price)}</span>
                          <span className="text-[#424E59] line-through">{copy.foundingMember.regularPrice(foundingMemberStatus.regular_price)}</span>
                        </div>
                        <p className="text-sm text-[#3A4A5A]">
                          {copy.foundingMember.benefitsText} • {copy.foundingMember.spotsRemaining(foundingMemberStatus.spots_remaining)}
                        </p>
                      </div>
                      
                      <a
                        href="https://www.paypal.com/ncp/payment/WRA6839Q3SQTW"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="founding-member-btn"
                      >
                        <Button
                          className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-6 py-5 shadow-lg transition-all duration-300 hover:scale-[1.02]"
                        >
                          Become a Founding Member — $199
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                      
                      <p className="text-[#424E59] text-sm mt-4 italic">
                        {copy.foundingMember.optionalNote}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </div>
            </div>
          </Card>
        )}

        {/* Closed State - when 50 members reached */}
        {foundingMemberStatus?.is_eligible && !foundingMemberStatus?.is_available && (
          <Card className="border-none shadow-md rounded-2xl">
            <CardContent className="p-6 text-center">
              <p className="text-[#424E59]">
                {copy.foundingMember.closedMessage}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Share Note */}
        <Card className="border-none shadow-sm rounded-2xl bg-[#A3B8A6]/20 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <CardContent className="p-6">
            <h4 className="font-serif text-lg text-[#1D2D44] mb-2">
              {copy.summary.sharingTitle}
            </h4>
            <p className="text-[#3A4A5A] text-sm leading-relaxed">
              {copy.summary.sharingDescription}
            </p>
          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="flex justify-center gap-4 mt-8">
          <Link to="/reflections">
            <Button 
              variant="outline"
              className="border-[#1D2D44] text-[#1D2D44] hover:bg-[#F5F5F5] rounded-full"
              data-testid="view-reflections-btn"
            >
              {copy.summary.viewReflections}
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button 
              className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full"
              data-testid="back-to-dashboard-btn"
            >
              {copy.summary.backToDashboard}
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#424E59] text-sm">
            {copy.summary.privacyFooter}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SummaryPage;
